import { getItem, removeItem, setItem } from '@alltrails/core/utils/sessionStorage';

const STORAGE_KEY = 'at:navigatedFromSearch';

// Strip an optional leading locale segment and lowercase
const normalizePath = (pathname: string): string => pathname.toLowerCase().replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/, '');

/**
 * Records that the user is navigating to `destinationUrl` by selecting a search
 * result. Stored in sessionStorage so it survives the full-page
 * `window.location.assign` that search navigation uses, and scoped to the
 * destination pathname so it only applies on that page — it persists across
 * interactions there but does not leak to a later, unrelated navigation.
 *
 * Read it back with {@link arrivedViaSearch}.
 */
export const markNavigatedFromSearch = (destinationUrl: string): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const { pathname } = new URL(destinationUrl, window.location.origin);
    setItem(STORAGE_KEY, normalizePath(pathname));
  } catch {
    // URL parsing can fail for a malformed url; navigation still proceeds.
  }
};

// Memoizes the result for the lifetime of the page so the stored flag can be
// consumed on first read (see below) while repeated calls during the same page
// load — multiple analytics events all calling getNavigatedFrom — still agree.
let resolved: boolean | undefined;

/**
 * Returns true when the current page was reached by selecting a search result
 * (see {@link markNavigatedFromSearch}). Matches on pathname so it stays true
 * across interactions on the destination page but not after navigating away.
 *
 * The flag is single-use: it is removed from sessionStorage on the first read
 * that matches, so a later navigation that returns to the same path via a
 * different route (e.g. the inspirational list carousel) is no longer
 * misattributed to search. Carousel/cross-app navigations are full document
 * loads, which reset the in-memory memo on each landing.
 */
export const arrivedViaSearch = (): boolean => {
  if (resolved !== undefined) {
    return resolved;
  }
  if (typeof window === 'undefined') {
    return false; // don't memoize during SSR; let the client resolve it
  }
  resolved = getItem<string>(STORAGE_KEY) === normalizePath(window.location.pathname);
  if (resolved) {
    removeItem(STORAGE_KEY);
  }
  return resolved;
};
